import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

let _index = 1;
export class OiqClientContactForm extends FormGroup {
  private _index = _index++;

  get index(): number {
    return this._index;
  }

  constructor(isPrimary: boolean) {
    super({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      isPrimaryContact: new FormControl(isPrimary ? { value: true, disabled: true } : false),
      isOwner: new FormControl(isPrimary ? { value: true, disabled: true } : true), // Default to true for additional contacts
      primary: new FormControl(
        isPrimary ? { value: true, disabled: true } : { value: false, disabled: false }
      ),

      // Additional contact fields
      ssn: new FormControl(''),
      dob: new FormControl(null),
      email: new FormControl(''),
      phoneNumber: new FormControl(''),
      cellNumber: new FormControl(''),
      citizenshipStatus: new FormControl('US_Citizen'),
      percent: new FormControl(isPrimary ? 100 : 0),

      // Contact address
      address: new FormGroup({
        address1: new FormControl(''),
        address2: new FormControl(''),
        city: new FormControl(''),
        state: new FormControl(''),
        zipCode: new FormControl(''),
        country: new FormControl('US'),
      }),
    });
  }

  ownerChecked(isChecked: boolean): void {
    const _owner = this.get('isOwner');
    const _primary = this.get('primary');

    _primary.markAsUntouched();
    if (!isChecked) {
      _primary.setValue(false);
    }

    isChecked ? _primary.enable() : _primary.disable();
  }

  clearPrimaryContact(): void {
    const _primary = this.get('isPrimaryContact');
    _primary.markAsUntouched();
    _primary.setValue(false);
  }

  clearPrimary(): void {
    const _primary = this.get('primary');
    _primary.markAsUntouched();
    _primary.setValue(false);
  }

  enablePrimaryContact(): void {
    const _primary = this.get('isPrimaryContact');
    _primary.enable();
  }

  enableOwner(): void {
    const _owner = this.get('isOwner');
    _owner.enable();
  }

  enablePrimary(): void {
    const _primary = this.get('primary');
    _primary.enable();
  }

  isOwnerChecked(): boolean {
    const _owner = this.get('isOwner');
    return _owner.value;
  }

  get toPayload() {
    const contact = this.getRawValue();
    // Check if contact address exists and has values
    const hasAddress =
      contact.address &&
      (contact.address.address1 ||
        contact.address.city ||
        contact.address.state ||
        contact.address.zipCode);

    return {
      firstName: contact.firstName,
      lastName: contact.lastName,
      isPrimaryContact: contact.isPrimaryContact,
      isOwner: contact.isOwner,
      primary: contact.primary,
      ssn: contact.ssn,
      dob: contact.dob,
      email: contact.email,
      phoneNumber: contact.phoneNumber,
      cellNumber: contact.cellNumber,
      citizenshipStatus: contact.citizenshipStatus,
      percent: contact.percent,
      address: hasAddress ? contact.address : null,
    };
  }
}

export class AddClientContactFormArray extends FormArray<OiqClientContactForm> {
  constructor() {
    super([]);
    this.addValidators(this._checkboxValidator.bind(this));

    this.add(true);
  }

  add(isPrimary: boolean = false): void {
    const _contact = new OiqClientContactForm(isPrimary);

    _contact.get('isPrimaryContact')?.valueChanges.subscribe(_checked => {
      const _control = _contact.get('isPrimaryContact');
      if (_control && _control.untouched) {
        return;
      }
      if (!_checked) {
        return;
      }
      this.setOnlyOnePrimaryContact(_contact.index);
    });

    _contact.get('isOwner')?.valueChanges.subscribe(_checked => {
      const _control = _contact.get('isOwner');
      if (_control && _control.untouched) {
        return;
      }
      _contact.ownerChecked(_checked);
    });

    _contact.get('primary')?.valueChanges.subscribe(_checked => {
      const _control = _contact.get('primary');
      if (_control && _control.untouched) {
        return;
      }
      if (!_checked) {
        return;
      }
      this.setOnlyOnePrimaryOwner(_contact.index);
    });

    this.push(_contact);

    if (this.length > 1) {
      const _firstContact = this.controls.at(0);
      // Only enable these controls if we have more than one contact
      _firstContact.enablePrimaryContact();
      _firstContact.enablePrimary();
      _firstContact.enableOwner();
    }
  }

  private setOnlyOnePrimaryContact(_index: number): void {
    this.controls.forEach(_contact => {
      if (_contact.index === _index) {
        return;
      }
      _contact.clearPrimaryContact();
    });
  }

  private setOnlyOnePrimaryOwner(_index: number): void {
    this.controls.forEach(_contact => {
      if (_contact.index !== _index) {
        _contact.clearPrimary();
      }
    });
  }

  get toPayload() {
    return this.controls.map(contact => contact.toPayload);
  }

  remove(clientContactIndex: number): void {
    this.removeAt(clientContactIndex);
    this.disableControlIfThereIsSingleContact();
  }

  private disableControlIfThereIsSingleContact(): void {
    if (this.controls.length > 1) {
      return;
    }
    const _firstContact = this.controls.at(0);
    _firstContact.get('isPrimaryContact')?.markAsUntouched();
    _firstContact.get('isOwner')?.markAsUntouched();
    _firstContact.get('primary')?.markAsUntouched();

    _firstContact.patchValue({
      isPrimaryContact: true,
      isOwner: true,
      primary: true,
    });
    _firstContact.get('isPrimaryContact')?.disable();
    _firstContact.get('isOwner')?.disable();
    _firstContact.get('primary')?.disable();
  }

  /**
   * Check if both primary owner and primary contact are selected
   */
  arePrimaryOwnerAndPrimaryContactSelected(): boolean {
    let isTherePrimaryContact = false;
    let isTherePrimaryOwner = false;
    this.toPayload.forEach(clientContact => {
      if (clientContact.isPrimaryContact) {
        isTherePrimaryContact = true;
      }
      if (clientContact.primary) {
        isTherePrimaryOwner = true;
      }
    });
    return isTherePrimaryOwner && isTherePrimaryContact;
  }

  /**
   * Check if at least one value is selected for each contact
   */
  isAtLeastOneValueSelectedForContact(): boolean {
    let validSelectedContactControls = 0;
    this.toPayload.forEach(clientContact => {
      if (clientContact.isPrimaryContact || clientContact.primary || clientContact.isOwner) {
        validSelectedContactControls++;
      }
    });
    return validSelectedContactControls === this.toPayload.length;
  }

  private _checkboxValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!this.arePrimaryOwnerAndPrimaryContactSelected()) {
      return {
        contactsValidationErrors: 'At least one Primary Contact or Primary Owner must be selected',
      };
    }

    if (!this.isAtLeastOneValueSelectedForContact()) {
      return { contactsValidationErrors: 'Each contact must have at least one role selected' };
    }

    return null;
  }
}
